import { User, Mail, Shield, Palette, Monitor, Sun, Moon } from 'lucide-preact'
import { useAuth } from '../../auth/useAuth'
import { useTheme } from '../../hooks/useTheme'

export function SettingsProfile() {
  const { userProfile, user } = useAuth()
  const { theme, setTheme, resolvedTheme } = useTheme()

  // Helper function to get system theme
  function getSystemTheme() {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return 'dark'
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          Profile Settings
        </h1>
        <p className="text-slate-600 dark:text-slate-300">
          Manage your personal information and preferences
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Information Card */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            Personal Information
          </h2>
          
          <div className="space-y-6">
            {/* Profile Picture and Name */}
            <div className="flex items-center gap-6">
              <div className="relative">
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt="Profile picture" 
                    className="h-20 w-20 rounded-full"
                  />
                ) : (
                  <div className="h-20 w-20 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                    <User className="h-10 w-10 text-slate-600 dark:text-slate-300" />
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                  {userProfile?.FullName || user?.name || 'Unknown User'}
                </h3>
                <p className="text-slate-600 dark:text-slate-300">
                  {userProfile?.PrimaryEmail || user?.email}
                </p>
              </div>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  First Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={userProfile?.FirstName || ''}
                    disabled
                    className="w-full px-3 py-2 pl-10 border border-slate-300 dark:border-slate-600 rounded-md bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white text-sm disabled:cursor-not-allowed"
                  />
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                </div>
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Last Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={userProfile?.LastName || ''}
                    disabled
                    className="w-full px-3 py-2 pl-10 border border-slate-300 dark:border-slate-600 rounded-md bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white text-sm disabled:cursor-not-allowed"
                  />
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                </div>
              </div>

              {/* Primary Email */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Primary Email
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={userProfile?.PrimaryEmail || user?.email || ''}
                    disabled
                    className="w-full px-3 py-2 pl-10 border border-slate-300 dark:border-slate-600 rounded-md bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white text-sm disabled:cursor-not-allowed"
                  />
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                </div>
              </div>

              {/* User ID */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  User ID
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={userProfile?.Auth0UserId || user?.sub || ''}
                    disabled
                    className="w-full px-3 py-2 pl-10 border border-slate-300 dark:border-slate-600 rounded-md bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white text-sm disabled:cursor-not-allowed font-mono"
                  />
                  <Shield className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                </div>
              </div>
            </div>

            {/* Additional Email Addresses */}
            {userProfile?.Emails && userProfile.Emails.length > 1 && (
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  Additional Email Addresses
                </h3>
                <div className="space-y-2">
                  {userProfile.Emails.filter(email => !email.IsPrimary).map((email, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-md">
                      <Mail className="h-4 w-4 text-slate-400" />
                      <span className="text-sm text-slate-900 dark:text-white">
                        {email.EmailAddress}
                      </span>
                      {email.IsVerified ? (
                        <span className="px-2 py-1 text-xs bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-400 rounded-full">
                          Verified
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-400 rounded-full">
                          Unverified
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Note */}
            <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-md">
              <p className="text-sm text-primary-800 dark:text-primary-400">
                <strong>Note:</strong> Profile information is managed through your Auth0 account. 
                To update your personal details, please contact your system administrator.
              </p>
            </div>
          </div>
        </div>

        {/* Theme Settings Card */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Palette className="h-5 w-5 text-slate-600 dark:text-slate-300" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Appearance
            </h2>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-6">
            Customize how the app looks and feels
          </p>

          {/* Theme Options */}
          <div className="space-y-3">
            {/* System Theme Option */}
            <button
              onClick={() => setTheme('system')}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                theme === 'system'
                  ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-700 text-primary-900 dark:text-primary-100'
                  : 'bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600'
              }`}
            >
              <Monitor className="h-5 w-5" />
              <div className="text-left flex-1">
                <div className="font-medium">System</div>
                <div className="text-sm opacity-70">
                  Use your device's setting ({getSystemTheme()})
                </div>
              </div>
              {theme === 'system' && (
                <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
              )}
            </button>

            {/* Light Theme Option */}
            <button
              onClick={() => setTheme('light')}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                theme === 'light'
                  ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-700 text-primary-900 dark:text-primary-100'
                  : 'bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600'
              }`}
            >
              <Sun className="h-5 w-5" />
              <div className="text-left flex-1">
                <div className="font-medium">Light</div>
                <div className="text-sm opacity-70">
                  Clean and bright interface
                </div>
              </div>
              {theme === 'light' && (
                <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
              )}
            </button>

            {/* Dark Theme Option */}
            <button
              onClick={() => setTheme('dark')}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                theme === 'dark'
                  ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-700 text-primary-900 dark:text-primary-100'
                  : 'bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600'
              }`}
            >
              <Moon className="h-5 w-5" />
              <div className="text-left flex-1">
                <div className="font-medium">Dark</div>
                <div className="text-sm opacity-70">
                  Easy on the eyes in low light
                </div>
              </div>
              {theme === 'dark' && (
                <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
              )}
            </button>
          </div>

          {/* Current Theme Display */}
          <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-600">
            <div className="text-sm text-slate-600 dark:text-slate-300">
              <span className="font-medium">Currently using:</span> {resolvedTheme === 'dark' ? 'Dark' : 'Light'} mode
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
